import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search, Brain, FileDown, CheckCircle2, ShieldCheck } from "lucide-react";
import Layout from "@/components/Layout";
import { phCapture } from "@/lib/posthog";

const Landing = () => {
  useEffect(() => { phCapture("landing_page_viewed", { route: "/landing" }); }, []);
  const generateLink = "/generate";

  return (
    <Layout>
      <div className="dark-page">
        {/* Hero */}
        <section className="px-4 py-16 md:py-24">
          <div className="mx-auto max-w-6xl px-4 md:px-6 text-center">
            <h1 className="text-4xl font-extrabold leading-tight tracking-tight md:text-5xl">
              Turn real buyer chatter into a copy pack{" "}
              <span className="gradient-text">in minutes.</span>
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-sm leading-7 text-slate-200/90 md:text-base">
              Paste keywords → we scan Reddit + web conversations → you get pain themes, objections, paste-ready copy + a PDF.
            </p>
            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Link
                to={generateLink}
                className="btn-gradient inline-flex w-full items-center justify-center gap-2 rounded-lg px-7 py-3 text-sm font-semibold transition sm:w-auto"
              >
                Generate a report <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex w-full items-center justify-center rounded-lg border border-white/10 px-7 py-3 text-sm font-semibold text-slate-400 transition hover:border-white/20 hover:text-white sm:w-auto"
              >
                See pricing
              </Link>
            </div>
            <p className="mt-4 text-xs text-slate-400">Amazon + Shopify copy packs, market tracking, and PDF export included.</p>
          </div>
        </section>

        {/* Social proof */}
        <section className="py-8">
          <p className="text-center text-sm text-slate-400">
            Built for marketers, founders, and sellers who need fast audience insight.
          </p>
        </section>

        {/* How it works */}
        <section className="py-12">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <h2 className="mb-8 text-center text-xl font-bold">
              How it works
            </h2>
            <div className="mx-auto grid max-w-3xl gap-6 md:grid-cols-3">
              {[
                { icon: Search, color: "text-blue-400 bg-blue-500/15", title: "Enter keywords + sources", desc: "Choose your search keywords and pick source types" },
                { icon: Brain, color: "text-teal-400 bg-teal-500/15", title: "We extract insights", desc: "Pain themes & objections pulled from real evidence" },
                { icon: FileDown, color: "text-emerald-400 bg-emerald-500/15", title: "Download your PDF", desc: "Get a client-ready copy pack you can use immediately" },
              ].map(({ icon: Icon, color, title, desc }) => (
                <div key={title} className="card-surface text-center">
                  <div className={`mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-xl ${color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mb-1 font-semibold text-white">{title}</h3>
                  <p className="text-sm leading-7 text-slate-200/90">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What you get */}
        <section className="py-12">
          <div className="mx-auto max-w-6xl px-4 md:px-6">
            <h2 className="mb-6 text-center text-xl font-bold">
              What you get
            </h2>
            <ul className="mx-auto max-w-md space-y-3">
              {[
                "Pain themes extracted from buyer conversations",
                "Sales Page Builder — Shopify-ready copy structure in one click",
                "Improve My Listing — paste your copy, get an optimised version",
                "Market Radar — see what changed since your last run",
                "Objections + reassurance angles mapped to FAQ",
                "Evidence-labeled sources with one-click Copy buttons",
                "PDF export of the full report",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm leading-7 text-slate-200/90">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-teal-400" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Trust note */}
        <section className="py-8">
          <div className="mx-auto max-w-6xl flex items-center justify-center gap-2 px-4 md:px-6 text-center text-sm text-slate-400">
            <ShieldCheck className="h-4 w-4 shrink-0 text-teal-400" />
            Evidence labels show whether sources are user voice or editorial. No invented claims.
          </div>
        </section>

        {/* Footer CTA */}
        <section className="py-12">
          <div className="mx-auto max-w-6xl px-4 md:px-6 text-center">
            <h2 className="mb-4 text-xl font-bold">
              Ready to start?
            </h2>
            <Link
              to={generateLink}
              className="btn-gradient inline-flex items-center gap-2 rounded-lg px-7 py-3 text-sm font-semibold transition"
            >
              Generate your first report <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default Landing;
